








































































































import { Vue, Component, Prop } from 'vue-property-decorator'
import { KbkItem } from './c-kbk-selector-types'

@Component
export default class CKbkSelector extends Vue {
    @Prop({ default: () => [] }) value!: number[]
    @Prop({ default: () => [] }) options!: KbkItem[]
    @Prop({ default: () => [] }) cascadeOptions!: KbkItem[]
    @Prop({ default: '' }) levelKey!: string
    @Prop({ default: '' }) levelLabel!: string
    @Prop({ default: false }) disabled!: boolean
    @Prop({ default: '' }) emptyText!: string
    @Prop({ default: 0 }) maxItems!: number

    public isOpen = false
    public searchQuery = ''

    private onDocumentMousedown: ((e: MouseEvent) => void) | null = null

    // --- Lifecycle ---

    mounted() {
        this.onDocumentMousedown = (e: MouseEvent) => {
            const root = this.$refs.root as HTMLElement | undefined
            if (root && !root.contains(e.target as Node)) {
                this.closePanel()
            }
        }
        document.addEventListener('mousedown', this.onDocumentMousedown)
    }

    beforeDestroy() {
        if (this.onDocumentMousedown) {
            document.removeEventListener('mousedown', this.onDocumentMousedown)
        }
    }

    // --- Computed ---

    get isAllCascadeSelected(): boolean {
        if (this.cascadeOptions.length === 0) return false
        return this.cascadeOptions.every(o => this.value.includes(o.code))
    }

    get countText(): string {
        const q = this.searchQuery.trim()
        const total = this.cascadeOptions.length
        if (q) {
            return this.tr('kbk.found_of', { n: this.filteredOptions.length, m: total })
        }
        return this.tr('kbk.values_count', { n: total })
    }

    get compactText(): string {
        const val = this.value
        const cascade = this.cascadeOptions

        // Состояние 1: ничего не выбрано
        if (val.length === 0) {
            return this.tr('kbk.selection_empty')
        }

        const cascadeCodes = new Set(cascade.map(o => o.code))
        const M = new Set([...cascadeCodes, ...val]).size
        const allCascadeSelected = cascade.length > 0 && cascade.every(o => val.includes(o.code))
        const noExtraSelected = val.every(c => cascadeCodes.has(c))

        // Состояние 2: "Все" — все cascade выбраны и нет несовместимых
        if (allCascadeSelected && noExtraSelected) {
            return this.tr('all')
        }

        // Состояние 3: "Все кроме"
        const unselectedInCascade = cascade.filter(o => !val.includes(o.code))
        const selectedInCascade = val.filter(c => cascadeCodes.has(c))
        if (unselectedInCascade.length > 0 && unselectedInCascade.length <= 20 && unselectedInCascade.length < selectedInCascade.length) {
            return this.tr('kbk.selected_all_except', { codes: unselectedInCascade.map(o => o.code).join(', ') })
        }

        // Состояние 4: ≤3 выбрано
        if (val.length <= 3) {
            return val.join(', ')
        }

        // Состояние 5: "Выбрано N из M"
        return this.tr('kbk.selected_count', { n: val.length, m: M })
    }

    get tooltipText(): string {
        if (this.value.length === 0) return ''
        const first20 = this.value.slice(0, 20)
        const rest = this.value.length - 20

        const parts = first20.map(code => {
            const item = this.options.find(o => o.code === code) || this.cascadeOptions.find(o => o.code === code)
            if (!item || item.nameEntries.length === 0) return String(code)
            if (item.nameEntries.length === 1) {
                const entry = item.nameEntries[0]
                return `${code} — ${this.localizedName(entry.name_ru, entry.name_kk)}`
            }
            return `${code} · ${this.tr('kbk.names_count', { n: item.nameEntries.length })}`
        })

        let result = parts.join('\n')
        if (rest > 0) {
            result += '\n' + this.tr('kbk.and_more', { n: rest })
        }
        return result
    }

    get cascadeCodesSet(): Set<number> {
        return new Set(this.cascadeOptions.map(o => o.code))
    }

    get optionsCodesSet(): Set<number> {
        return new Set(this.options.map(o => o.code))
    }

    /**
     * Коды из selected, которых нет ни в options, ни в cascadeOptions.
     * Показываются серыми в конце списка; клик снимает выбор.
     */
    get ghostItems(): Array<{ code: number; name: string }> {
        return this.value
            .filter(code => !this.optionsCodesSet.has(code))
            .map(code => ({ code, name: '' }))
    }

    get filteredOptions(): KbkItem[] {
        const q = this.searchQuery.trim().toLowerCase()
        if (!q) return this.options
        return this.options.filter(item => {
            if (String(item.code).startsWith(q)) return true
            return item.nameEntries.some(e =>
                e.name_ru.toLowerCase().includes(q) ||
                e.name_kk.toLowerCase().includes(q)
            )
        })
    }

    // --- Methods ---

    private get currentLocale(): string {
        return (this.$root as any).$i18n?.locale ?? 'ru'
    }

    private tr(key: string, params?: Record<string, string | number>): string {
        const fullKey = `modules.budget_execution_monitoring.reports_constructor.${key}`
        return this.$t(fullKey, params) as string
    }

    private localizedName(name_ru: string, name_kk: string): string {
        return this.currentLocale === 'kk' ? name_kk : name_ru
    }

    // Показывать счётчик наименований? — когда > 1 имён и поиска нет
    public isCountOnlyItem(item: KbkItem): boolean {
        return item.nameEntries.length > 1 && !this.searchQuery.trim()
    }

    // Текст счётчика: "наименований: N"
    public getItemCountText(item: KbkItem): string {
        return this.tr('kbk.names_count', { n: item.nameEntries.length })
    }

    // Имя для показа в строке
    public getItemName(item: KbkItem): string {
        const q = this.searchQuery.trim().toLowerCase()
        if (item.nameEntries.length === 0) return ''
        if (!q || item.nameEntries.length === 1) {
            const e = item.nameEntries[0]
            return this.localizedName(e.name_ru, e.name_kk)
        }
        // При поиске — найти совпадение
        const matched = item.nameEntries.find(e =>
            e.name_ru.toLowerCase().includes(q) || e.name_kk.toLowerCase().includes(q)
        )
        const e = matched || item.nameEntries[0]
        return this.localizedName(e.name_ru, e.name_kk)
    }

    // Бэджи аннотаций (только при поиске + неоднозначность + совпадение)
    public getItemBadges(item: KbkItem): string[] {
        const q = this.searchQuery.trim().toLowerCase()
        if (!q || item.nameEntries.length <= 1) return []
        const matched = item.nameEntries.find(e =>
            e.name_ru.toLowerCase().includes(q) || e.name_kk.toLowerCase().includes(q)
        )
        const e = matched || item.nameEntries[0]
        if (!e || e.parents.length === 0) return []
        return e.parents.map(p => `${this.tr(p.levelKey)} ${p.code}`)
    }

    // Число "ещё N" — все остальные наименования кроме показанного
    public getItemExtra(item: KbkItem): number {
        const q = this.searchQuery.trim().toLowerCase()
        if (!q || item.nameEntries.length <= 1) return 0
        return Math.max(0, item.nameEntries.length - 1)
    }

    public isSelected(code: number): boolean {
        return this.value.includes(code)
    }

    public isIncompatible(code: number): boolean {
        if (this.cascadeOptions.length === 0) return false
        return !this.cascadeCodesSet.has(code)
    }

    public toggleItem(code: number): void {
        const current = [...this.value]
        const idx = current.indexOf(code)
        if (idx >= 0) {
            current.splice(idx, 1)
        } else {
            if (this.maxItems === 1) {
                this.$emit('input', [code])
                return
            }
            current.push(code)
        }
        this.$emit('input', current)
    }

    public toggleAll(): void {
        if (this.isAllCascadeSelected) {
            this.$emit('input', [])
        } else {
            this.$emit('input', this.cascadeOptions.map(o => o.code))
        }
    }

    public clearSelection(): void {
        this.$emit('input', [])
    }

    public togglePanel(): void {
        if (this.isOpen) {
            this.closePanel()
        } else {
            this.openPanel()
        }
    }

    public openPanel(): void {
        this.isOpen = true
        this.searchQuery = ''
        this.$nextTick(() => {
            const input = this.$refs.searchInput as HTMLInputElement | undefined
            if (input) input.focus()
        })
    }

    public closePanel(): void {
        this.isOpen = false
        this.searchQuery = ''
    }
}
